import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ContactService } from '../../services/contact.service';
import { ToastService } from '../../shared/toast/toast.service';
import { Router } from '@angular/router';
import { SecurityService } from '../../services/security.service';

@Component({
  selector: 'app-sell-property',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './sell-property.component.html',
  styleUrl: './sell-property.component.css'
})
export class SellPropertyComponent {
  // Form fields - Contact Info
  name = '';
  email = '';
  country_code = '+961';
  phone = '';
  
  // Basic Property Info
  propertyType = '';
  address = '';
  city = '';
  state = '';
  zipCode = '';
  bedrooms = '';
  bathrooms = '';
  squareFeet = '';
  yearBuilt = '';
  currentValue = '';
  
  // Additional Property Details
  neighborhood = '';
  floor = '';
  parkingCovered = '';
  parkingOutdoor = '';
  hasTerrace = false;
  hasGarden = false;
  hasView = false;
  hasElevator = false;
  isFurnished = false;
  generalCondition = '';
  additionalInfo = '';

  errors = signal<{ [key: string]: string }>({});
  isSubmitting = signal(false);
  
  countryCodes = [
    { code: '+961', country: 'Lebanon', flag: '🇱🇧' },
    { code: '+1', country: 'United States', flag: '🇺🇸' },
    { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+971', country: 'UAE', flag: '🇦🇪' },
    { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
    { code: '+20', country: 'Egypt', flag: '🇪🇬' },
    { code: '+962', country: 'Jordan', flag: '🇯🇴' },
    { code: '+90', country: 'Turkey', flag: '🇹🇷' },
    { code: '+34', country: 'Spain', flag: '🇪🇸' },
    { code: '+39', country: 'Italy', flag: '🇮🇹' },
    { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
    { code: '+32', country: 'Belgium', flag: '🇧🇪' },
    { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
    { code: '+46', country: 'Sweden', flag: '🇸🇪' },
    { code: '+47', country: 'Norway', flag: '🇳🇴' },
    { code: '+45', country: 'Denmark', flag: '🇩🇰' },
    { code: '+43', country: 'Austria', flag: '🇦🇹' },
    { code: '+30', country: 'Greece', flag: '🇬🇷' },
    { code: '+351', country: 'Portugal', flag: '🇵🇹' },
    { code: '+91', country: 'India', flag: '🇮🇳' },
    { code: '+86', country: 'China', flag: '🇨🇳' },
    { code: '+81', country: 'Japan', flag: '🇯🇵' },
    { code: '+82', country: 'South Korea', flag: '🇰🇷' },
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+64', country: 'New Zealand', flag: '🇳🇿' },
    { code: '+55', country: 'Brazil', flag: '🇧🇷' },
    { code: '+52', country: 'Mexico', flag: '🇲🇽' },
    { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  ];

  constructor(
    private contactService: ContactService,
    private toastService: ToastService,
    private router: Router,
    private securityService: SecurityService
  ) {}

  validateForm(): boolean {
    const newErrors: { [key: string]: string } = {};

    // Rate limiting
    if (!this.securityService.checkRateLimit('sell_property_form', 5)) {
      this.toastService.error('Too many requests. Please try again later.');
      return false;
    }

    // Length validation
    if (this.name && !this.securityService.isValidLength(this.name, 255)) {
      newErrors['name'] = 'Name is too long';
    }
    if (this.email && !this.securityService.isValidLength(this.email, 255)) {
      newErrors['email'] = 'Email is too long';
    }
    if (this.additionalInfo && !this.securityService.isValidLength(this.additionalInfo, 5000)) {
      newErrors['additionalInfo'] = 'Additional information is too long';
    }

    // Personal Information
    if (!this.name.trim()) {
      newErrors['name'] = 'Name is required';
    }
    if (!this.email.trim()) {
      newErrors['email'] = 'Email is required';
    } else if (!this.securityService.isValidEmail(this.email)) {
      newErrors['email'] = 'Please enter a valid email';
    }
    if (!this.country_code.trim()) {
      newErrors['country_code'] = 'Country code is required';
    }
    if (!this.phone.trim()) {
      newErrors['phone'] = 'Phone number is required';
    } else if (!this.securityService.isValidPhone(this.phone)) {
      newErrors['phone'] = 'Please enter a valid phone number';
    }

    // Property Information
    if (!this.propertyType) {
      newErrors['propertyType'] = 'Property type is required';
    }
    if (!this.address.trim()) {
      newErrors['address'] = 'Address is required';
    }
    if (!this.city.trim()) {
      newErrors['city'] = 'City is required';
    }
    if (!this.state.trim()) {
      newErrors['state'] = 'State is required';
    }
    if (!this.zipCode.trim()) {
      newErrors['zipCode'] = 'ZIP code is required';
    }

    this.errors.set(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async submitForm() {
    if (!this.validateForm()) {
      this.toastService.show('error', 'Please fill in all required fields');
      return;
    }

    this.isSubmitting.set(true);

    const fullPhone = `${this.country_code} ${this.phone}`;
    const propertyDetails = `
Property Type: ${this.propertyType}
Address: ${this.address}, ${this.city}, ${this.state} ${this.zipCode}
Neighborhood: ${this.neighborhood || 'Not specified'}
Floor: ${this.floor || 'Not specified'}
Bedrooms: ${this.bedrooms || 'Not specified'}
Bathrooms: ${this.bathrooms || 'Not specified'}
Square Feet: ${this.squareFeet || 'Not specified'}
Year Built: ${this.yearBuilt || 'Not specified'}
Estimated Value: ${this.currentValue || 'Not specified'}

Property Features:
- Parking (Covered): ${this.parkingCovered || '0'}
- Parking (Outdoor): ${this.parkingOutdoor || '0'}
- Terrace: ${this.hasTerrace ? 'Yes' : 'No'}
- Garden: ${this.hasGarden ? 'Yes' : 'No'}
- View: ${this.hasView ? 'Yes' : 'No'}
- Elevator: ${this.hasElevator ? 'Yes' : 'No'}
- Furnished: ${this.isFurnished ? 'Yes' : 'No'}
- General Condition: ${this.generalCondition || 'Not specified'}

Additional Information:
${this.additionalInfo || 'None provided'}
    `.trim();

    const message = `PROPERTY SELLING INQUIRY\n\n${propertyDetails}`;

    try {
      await this.contactService.submitContactForm({
        name: this.securityService.sanitizeFormInput(this.name, 255),
        email: this.securityService.sanitizeFormInput(this.email, 255),
        country_code: this.securityService.sanitizeFormInput(this.country_code, 20),
        phone: this.securityService.sanitizeFormInput(this.phone, 50),
        subject: 'Sell Property',
        message: this.securityService.sanitizeFormInput(message, 5000)
      });

      this.toastService.show(
        'success',
        'Your property listing request has been submitted! We\'ll contact you soon.'
      );

      // Reset form
      this.resetForm();

      // Navigate to home after a delay
      setTimeout(() => {
        this.router.navigate(['/home']);
      }, 2000);
    } catch (error) {
      console.error('Error submitting form:', error);
      this.toastService.show(
        'error',
        'Failed to submit your request. Please try again.'
      );
    } finally {
      this.isSubmitting.set(false);
    }
  }

  resetForm() {
    this.name = '';
    this.email = '';
    this.country_code = '+961';
    this.phone = '';
    this.propertyType = '';
    this.address = '';
    this.city = '';
    this.state = '';
    this.zipCode = '';
    this.bedrooms = '';
    this.bathrooms = '';
    this.squareFeet = '';
    this.yearBuilt = '';
    this.currentValue = '';
    this.neighborhood = '';
    this.floor = '';
    this.parkingCovered = '';
    this.parkingOutdoor = '';
    this.hasTerrace = false;
    this.hasGarden = false;
    this.hasView = false;
    this.hasElevator = false;
    this.isFurnished = false;
    this.generalCondition = '';
    this.additionalInfo = '';
    this.errors.set({});
  }
}
